import React, { useState, useEffect } from 'react';
import LocationPicker from '../LocationPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditServiceModal = ({
  service,
  images,
  categories,
  uploading,
  saving,
  user,
  onServiceChange,
  onImageUpload,
  onImageRemove,
  onSave,
  onClose
}) => {
  const [locationOption, setLocationOption] = useState('current'); // 'current' | 'registered' | 'custom'

  // Build user's registered address
  const getRegisteredAddress = () => {
    if (!user) return '';
    const parts = [
      user.street_address,
      user.barangay,
      user.city,
      user.province,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : user.location || '';
  };

  const registeredAddress = getRegisteredAddress();

  useEffect(() => {
    // Default to 'current' (keep existing location)
    setLocationOption('current');
  }, [service?.id]);

  if (!service) return null;

  const handleLocationOptionChange = (option) => {
    setLocationOption(option);
    if (option === 'registered' && registeredAddress) {
      onServiceChange({ ...service, location: registeredAddress });
    } else if (option === 'custom') {
      onServiceChange({ ...service, location: '' });
    }
    // 'current' keeps service.location as-is
  };

  const handleLocationSelect = (selectedLocation) => {
    if (selectedLocation) {
      onServiceChange({ ...service, location: selectedLocation.name });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="edit-service-modal">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Edit Service</h2>
        <form onSubmit={onSave}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Title</label>
              <input
                type="text"
                value={service.title || ''}
                onChange={(e) => onServiceChange({ ...service, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Category</label>
              <select
                value={service.category || ''}
                onChange={(e) => onServiceChange({ ...service, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id || cat.name} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold mb-1">Description</label>
            <textarea
              value={service.description || ''}
              onChange={(e) => onServiceChange({ ...service, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Price</label>
              <input
                type="number"
                value={service.price || ''}
                onChange={(e) => onServiceChange({ ...service, price: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Currency</label>
              <select
                value={service.currency || 'PHP'}
                onChange={(e) => onServiceChange({ ...service, currency: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="PHP">PHP (₱)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Price Unit</label>
              <select
                value={service.price_unit || 'hour'}
                onChange={(e) => onServiceChange({ ...service, price_unit: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="hour">Per Hour</option>
                <option value="day">Per Day</option>
                <option value="project">Per Project</option>
                <option value="session">Per Session</option>
              </select>
            </div>
          </div>

          {/* Session Duration */}
          {service.price_unit === 'session' && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="block text-sm font-semibold mb-1 text-blue-800">
                Session Duration (Hours) <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-blue-600 mb-2">
                Specify how long each session lasts.
              </p>
              <select
                value={service.session_duration || '1'}
                onChange={(e) => onServiceChange({ ...service, session_duration: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                required
              >
                <option value="0.5">30 minutes</option>
                <option value="1">1 hour</option>
                <option value="1.5">1.5 hours</option>
                <option value="2">2 hours</option>
                <option value="2.5">2.5 hours</option>
                <option value="3">3 hours</option>
                <option value="4">4 hours</option>
                <option value="5">5 hours</option>
                <option value="6">6 hours</option>
              </select>
            </div>
          )}

          {/* Location Section with Options */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Service Location</label>

            <div className="space-y-2 mb-3">
              {/* Option 1: Keep current location */}
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="location-option-current">
                <input
                  type="radio"
                  name="editLocationOption"
                  checked={locationOption === 'current'}
                  onChange={() => handleLocationOptionChange('current')}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-800 text-sm">Keep current location</p>
                  <p className="text-xs text-gray-500">{service.location || 'No location set'}</p>
                </div>
              </label>

              {/* Option 2: Use registered address */}
              {registeredAddress && (
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="location-option-registered">
                  <input
                    type="radio"
                    name="editLocationOption"
                    checked={locationOption === 'registered'}
                    onChange={() => handleLocationOptionChange('registered')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-gray-800 text-sm">Use my registered address</p>
                    <p className="text-xs text-gray-500">{registeredAddress}</p>
                  </div>
                </label>
              )}

              {/* Option 3: Pick a different location */}
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" data-testid="location-option-custom">
                <input
                  type="radio"
                  name="editLocationOption"
                  checked={locationOption === 'custom'}
                  onChange={() => handleLocationOptionChange('custom')}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-800 text-sm">Use a different location</p>
                  <p className="text-xs text-gray-500">Search or pick from map</p>
                </div>
              </label>
            </div>

            {/* Show location picker when custom is selected */}
            {locationOption === 'custom' && (
              <LocationPicker
                onLocationSelect={handleLocationSelect}
                showRadius={false}
                placeholder="Select where you provide this service..."
              />
            )}

            {/* Show selected location */}
            {service.location && (
              <div className="flex items-center gap-2 mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-sm text-green-700 font-medium">{service.location}</span>
              </div>
            )}
          </div>

          {/* Service Images Section */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">Service Images</label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {images && images.length > 0 ? (
                images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`}
                      alt={`Service ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border"
                    />
                    {index === 0 && (
                      <span className="absolute top-1 left-1 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">
                        Main
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => onImageRemove(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition"
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <div className="col-span-4 text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                  No images uploaded yet
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Photo
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onImageUpload}
                  className="hidden"
                  disabled={uploading || (images && images.length >= 10)}
                />
              </label>
              {uploading && <span className="text-sm text-gray-500">Uploading...</span>}
              <span className="text-xs text-gray-500">{images?.length || 0}/10 images</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditServiceModal;
